import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  FlaskConical,
  Printer,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  Box
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Link } from 'react-router-dom';

const typeColors = {
  fdm: { bg: 'from-orange-500/30 to-orange-600/10', border: 'border-orange-500/40', text: 'text-orange-400', badge: 'bg-orange-600' },
  resin: { bg: 'from-cyan-500/30 to-cyan-600/10', border: 'border-cyan-500/40', text: 'text-cyan-400', badge: 'bg-cyan-700' },
};

const typeIcons = { fdm: Printer, resin: FlaskConical };

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'fdm', label: 'FDM' },
  { id: 'resin', label: 'Resin / MSLA' },
];

const PostProcessing = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/printing3d/post-processing`)
      .then(res => setEntries(res.data || []))
      .catch(err => console.error('Failed to load post-processing guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpanded = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const filtered = filter === 'all' ? entries : entries.filter(e => e.printer_type === filter);

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.PRINTING3D_POST_PROCESSING)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading post-processing guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="post-processing" style={bgStyle(IMAGES.PRINTING3D_POST_PROCESSING)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/3d-printing" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Sparkles className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">FINISHING GUIDE</div>
            <h1
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Post Processing
            </h1>
            <p className="text-sm text-zinc-300 mt-1">Sanding, priming, painting, acetone smoothing, resin washing, UV curing</p>
          </div>
        </div>
      </div>

      {/* Resin safety banner */}
      {(filter === 'resin' || filter === 'all') && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-900/40 border border-red-500/50 mb-6">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 font-semibold text-sm mb-1">Resin Safety — Read Before Proceeding</p>
            <p className="text-red-200 text-sm">
              Uncured resin is toxic and a skin sensitiser. Wear nitrile gloves and eye protection at all times.
              Work in a ventilated area. Never dispose of liquid resin or IPA/resin mix down the drain —
              cure all liquid waste under UV until solid, then dispose as solid waste.
            </p>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {FILTERS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === id ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Guide entries */}
      <div className="space-y-4 max-w-4xl">
        {filtered.map((entry) => {
          const colors = typeColors[entry.printer_type] || typeColors.fdm;
          const Icon = typeIcons[entry.printer_type] || Printer;
          const isOpen = expanded[entry.id];

          return (
            <Card key={entry.id} className={`bg-zinc-900 border-zinc-800`}>
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => toggleExpanded(entry.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-zinc-800 ${colors.text}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${colors.badge} text-white text-xs capitalize`}>
                          {entry.printer_type}
                        </Badge>
                      </div>
                      <CardTitle className="text-white text-lg">{entry.title}</CardTitle>
                      <CardDescription className="text-zinc-400 text-sm mt-0.5">{entry.summary}</CardDescription>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  )}
                </div>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-6 pt-0">
                  {/* Steps */}
                  {entry.steps && entry.steps.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-3">Steps</h4>
                      <ol className="space-y-3">
                        {entry.steps.map((step, idx) => (
                          <li key={idx} className="flex gap-3">
                            <span className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {step.step || idx + 1}
                            </span>
                            <div className="pt-0.5">
                              {step.grit && (
                                <span className="text-orange-400 text-sm font-semibold mr-2">{step.grit}</span>
                              )}
                              <span className="text-zinc-300 text-sm">{step.action}</span>
                              {step.duration && (
                                <span className="text-zinc-500 text-xs ml-2">({step.duration})</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* UV cure times (resin curing) */}
                  {entry.cure_times && entry.cure_times.length > 0 && (
                    <div>
                      <h4 className="text-white font-semibold mb-3">Cure Times by Resin Type</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {entry.cure_times.map((ct, idx) => (
                          <div key={idx} className="p-3 bg-zinc-800/60 rounded-lg">
                            <div className="text-zinc-400 text-xs mb-1">{ct.resin_type}</div>
                            <div className="text-white font-semibold text-sm">{ct.time}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Materials needed */}
                    {entry.materials_needed && entry.materials_needed.length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                          <Box className="w-4 h-4 text-blue-400" />
                          Materials Needed
                        </h4>
                        <ul className="space-y-1">
                          {entry.materials_needed.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-zinc-300 text-sm">
                              <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Tips */}
                    {entry.tips && entry.tips.length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-yellow-400" />
                          Tips
                        </h4>
                        <ul className="space-y-1">
                          {entry.tips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-zinc-300 text-sm">
                              <span className="text-yellow-400 flex-shrink-0">•</span>
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Safety notes */}
                  {entry.safety_notes && entry.safety_notes.length > 0 && (
                    <div className="p-4 rounded-xl bg-red-900/30 border border-red-500/40">
                      <h4 className="text-red-300 font-semibold text-sm mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Safety Notes
                      </h4>
                      <ul className="space-y-1">
                        {entry.safety_notes.map((note, idx) => (
                          <li key={idx} className="text-red-200 text-sm flex items-start gap-2">
                            <span className="flex-shrink-0">•</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No entries for this filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostProcessing;
